import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Standard API client (60s timeout - doubled)
const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000, // 60 seconds (was 30s)
});

// Long-running operations client (10 min timeout - doubled)
const apiLong = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 600000, // 10 minutes for email operations (was 5 min)
});

// Add retry logic for failed requests (network errors, timeouts)
const retryRequest = async (fn, retries = 2, delay = 1000) => {
  for (let i = 0; i <= retries; i++) {
    try {
      return await fn();
    } catch (error) {
      const isNetworkError = !error.response && error.code !== 'ECONNABORTED';
      const isServerError = error.response?.status >= 500;
      
      if ((isNetworkError || isServerError) && i < retries) {
        console.log(`Retry ${i + 1}/${retries} after error:`, error.message);
        await new Promise(r => setTimeout(r, delay * (i + 1)));
        continue;
      }
      throw error;
    }
  }
};

// Health check
export const checkHealth = async () => {
  try {
    const response = await api.get('/health', { timeout: 15000 });
    return response.data;
  } catch {
    return { status: 'error' };
  }
};

export const ticketService = {
  // Get all tickets for a project
  getTickets: async (project = 'WORLD CUP 2026', params = {}) => {
    const response = await api.get('/tickets', { params: { project, ...params } });
    return response.data;
  },

  // Get tickets with pagination
  getTicketsPaginated: async (project = 'WORLD CUP 2026', page = 1, pageSize = 100, params = {}) => {
    const response = await api.get('/tickets/paginated', { 
      params: { project, page, page_size: pageSize, ...params } 
    });
    return response.data;
  },

  // Get stats for a project
  getStats: async (project = 'WORLD CUP 2026') => {
    const response = await api.get('/stats', { params: { project } });
    return response.data;
  },

  // Get delta for a project
  getDelta: async (project = 'WORLD CUP 2026') => {
    const response = await api.get('/delta', { params: { project } });
    return response.data;
  },

  // Get filter values for columns
  getFilterValues: async (project = 'WORLD CUP 2026', type = null) => {
    const params = { project };
    if (type) params.type = type;
    const response = await api.get('/tickets/filter-values', { params });
    return response.data;
  },

  // Get single ticket
  getTicket: async (id) => {
    const response = await api.get(`/tickets/${id}`);
    return response.data;
  },

  // Create ticket
  createTicket: async (data) => {
    const response = await api.post('/tickets', data);
    return response.data;
  },

  // Update ticket
  updateTicket: async (id, data) => {
    const response = await api.put(`/tickets/${id}`, data);
    return response.data;
  },

  // Delete ticket
  deleteTicket: async (id) => {
    const response = await api.delete(`/tickets/${id}`);
    return response.data;
  },

  // Bulk delete tickets
  bulkDeleteTickets: async (ticketIds = [], deleteAll = false, typeFilter = null, project = 'WORLD CUP 2026', forceDelete = false) => {
    const response = await api.post('/tickets/bulk-delete', {
      ticket_ids: ticketIds,
      delete_all: deleteAll,
      type_filter: typeFilter,
      project: project,
      force_delete: forceDelete,
    });
    return response.data;
  },

  // Get match names mapping (match number -> full event name)
  getMatchNames: async (project = 'WORLD CUP 2026') => {
    try {
      const response = await api.get(`/match-names?project=${encodeURIComponent(project)}`);
      return response.data.match_names || {};
    } catch {
      return {};
    }
  },

  // AI Smart Import Analysis
  smartImportAnalyze: async (rawData, importType = 'ACHAT', useAi = true, project = 'ROLAND GARROS') => {
    const response = await api.post('/import/smart-analyze', {
      raw_data: rawData,
      import_type: importType,
      use_ai: useAi,
      project: project,
    });
    return response.data;
  },

  // Parse email
  parseEmail: async (emailContent, subject = '', fromEmail = '', project = 'WORLD CUP 2026') => {
    const response = await api.post('/tickets/parse', {
      email_content: emailContent,
      subject,
      from_email: fromEmail,
      project: project,
    });
    return response.data;
  },

  // Trigger matching
  runMatching: async () => {
    const response = await api.post('/tickets/match');
    return response.data;
  },

  // Unmatch ticket
  unmatchTicket: async (id) => {
    const response = await api.post(`/tickets/${id}/unmatch`);
    return response.data;
  },

  // Mark ticket as attribue
  markAttribue: async (id, attribue = true) => {
    const response = await api.post(`/tickets/${id}/attribue`, null, { params: { attribue } });
    return response.data;
  },

  // Validate ticket (and all related tickets) - marks as VALIDE (green)
  validateTicket: async (id) => {
    const response = await api.post(`/tickets/${id}/validate`);
    return response.data;
  },

  // Désattribuer all tickets for a project
  desattribuerAll: async (project = 'WORLD CUP 2026') => {
    const response = await api.post('/tickets/desattribuer-tout', null, { params: { project } });
    return response.data;
  },

  // Auto-match tickets (MATCHER) for a project - OPTIMIZED with BULK operations
  // Supports 20,000+ tickets
  autoMatch: async (project = 'WORLD CUP 2026') => {
    // Use skip_snapshot=true for faster execution
    const response = await apiLong.post('/tickets/auto-match', null, { 
      params: { project, skip_snapshot: true },
      timeout: 600000 // 10 minutes timeout for large datasets (20K+ tickets)
    });
    return response.data;
  },

  // Auto-match tickets with source exclusions
  // Same as autoMatch but ignores specified ACHAT sources
  autoMatchFiltered: async (project = 'WORLD CUP 2026', excludedSources = []) => {
    const response = await apiLong.post('/tickets/auto-match-filtered', {
      project,
      excluded_sources: excludedSources,
      skip_snapshot: true
    }, {
      timeout: 600000 // 10 minutes timeout
    });
    return response.data;
  },

  // Bulk validate transactions - pass VENTE + ACHAT to VALIDE status
  bulkValidate: async (references) => {
    const response = await api.post('/tickets/bulk-validate', { references });
    return response.data;
  },

  // Bulk import tickets for a project - OPTIMIZED
  bulkImport: async (tickets, project = 'WORLD CUP 2026') => {
    const response = await apiLong.post('/tickets/bulk', { tickets, project }, {
      params: { skip_snapshot: true },
      timeout: 180000 // 3 minutes timeout
    });
    return response.data;
  },

  // Connect email with IMAP
  connectEmail: async (email, appPassword, imapServer = '', imapPort = 993) => {
    const response = await api.post('/email/connect', { 
      email, 
      app_password: appPassword,
      imap_server: imapServer || undefined,
      imap_port: imapPort
    });
    return response.data;
  },

  // Check and fetch emails
  checkEmails: async (project = 'WORLD CUP 2026') => {
    const response = await api.post('/email/check', null, { params: { project } });
    return response.data;
  },

  getEmailStatus: async () => {
    const response = await api.get('/email/status');
    return response.data;
  },

  disconnectEmail: async () => {
    const response = await api.post('/email/disconnect');
    return response.data;
  },

  // Get logs (kept for reference, getStats and getDelta with project support are defined above)

  // Get logs
  getLogs: async (limit = 100) => {
    const response = await api.get('/logs', { params: { limit } });
    return response.data;
  },

  // Clear logs
  clearLogs: async () => {
    const response = await api.delete('/logs');
    return response.data;
  },

  // Get platforms
  getPlatforms: async () => {
    const response = await api.get('/platforms');
    return response.data;
  },

  // ==================== PROJECTS ====================
  
  // Get all projects
  getProjects: async () => {
    const response = await api.get('/projects');
    return response.data;
  },

  // Create a new project
  createProject: async (name, description = '') => {
    const response = await api.post('/projects', { name, description });
    return response.data;
  },

  // Delete a project
  deleteProject: async (projectName, deleteTickets = false) => {
    const response = await api.delete(`/projects/${encodeURIComponent(projectName)}`, {
      params: { delete_tickets: deleteTickets }
    });
    return response.data;
  },

  // Get project stats
  getProjectStats: async (projectName) => {
    const response = await api.get(`/projects/${encodeURIComponent(projectName)}/stats`);
    return response.data;
  },

  // ==================== INBOX ====================
  
  // Get inbox emails
  getInbox: async (project = null, processed = false) => {
    const params = { processed };
    if (project) params.project = project;
    const response = await api.get('/inbox', { params });
    return response.data;
  },

  // Get inbox count
  getInboxCount: async () => {
    const response = await api.get('/inbox/count');
    return response.data;
  },

  // Process single inbox item
  processInboxItem: async (itemId, project = 'WORLD CUP 2026') => {
    const response = await api.post(`/inbox/process/${itemId}`, null, { params: { project } });
    return response.data;
  },

  // Process all inbox items
  processAllInbox: async (project = 'WORLD CUP 2026') => {
    const response = await api.post('/inbox/process-all', null, { params: { project } });
    return response.data;
  },

  // Delete inbox item
  deleteInboxItem: async (itemId) => {
    const response = await api.delete(`/inbox/${itemId}`);
    return response.data;
  },

  // Clear inbox
  clearInbox: async (processedOnly = true) => {
    const response = await api.delete('/inbox', { params: { processed_only: processedOnly } });
    return response.data;
  },

  // ==================== DOUBLONS (Duplicates) ====================
  
  // Detect duplicate attributions
  getDoublons: async (project = 'WORLD CUP 2026') => {
    const response = await api.get('/tickets/doublons', { params: { project } });
    return response.data;
  },
  
  // Fix a specific doublon
  fixDoublon: async (refVente, ticketIdsToKeep, project = 'WORLD CUP 2026') => {
    const response = await api.post('/tickets/fix-doublon', null, { 
      params: { ref_vente: refVente, project },
      paramsSerializer: params => {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach(v => searchParams.append(key, v));
          } else {
            searchParams.append(key, value);
          }
        });
        return searchParams.toString();
      }
    });
    return response.data;
  },

  // ==================== MULTI-ACCOUNT EMAIL ====================
  
  // Get all email accounts
  getEmailAccounts: async () => {
    const response = await api.get('/email/accounts');
    return response.data;
  },

  // Add email account
  addEmailAccount: async (account) => {
    const response = await api.post('/email/accounts', account);
    return response.data;
  },

  // Delete email account
  deleteEmailAccount: async (accountId) => {
    const response = await api.delete(`/email/accounts/${accountId}`);
    return response.data;
  },

  // Preview emails - LONG OPERATION (uses apiLong for 2min timeout)
  previewEmails: async (maxEmails = 50, dateFilter = null, keyword = null) => {
    const params = { max_emails: maxEmails };
    if (dateFilter) params.date_filter = dateFilter;
    if (keyword) params.keyword = keyword;
    
    const response = await apiLong.post('/email/preview', null, { params });
    return response.data;
  },

  // Import selected emails only (auto-detect project from event name) - SUPPORTS ASYNC MODE
  importSelectedEmails: async (emailIds, project = 'WORLD CUP 2026', onProgress = null) => {
    const response = await api.post('/email/import-selected', { 
      email_ids: emailIds,
      project: project
    });
    
    const result = response.data;
    
    // If async mode, poll for progress
    if (result.mode === 'async' && result.task_id) {
      return await ticketService.pollImportTask(result.task_id, onProgress);
    }
    
    return result;
  },

  // Poll import task status
  pollImportTask: async (taskId, onProgress = null) => {
    const maxPolls = 600; // 10 minutes max (1s interval) - DOUBLED
    let pollCount = 0;
    
    while (pollCount < maxPolls) {
      try {
        const response = await api.get(`/email/import-status/${taskId}`);
        const status = response.data;
        
        // Call progress callback if provided
        if (onProgress) {
          onProgress(status);
        }
        
        // Check if completed or failed
        if (status.status === 'completed') {
          return {
            success: true,
            mode: 'async',
            task_id: taskId,
            tickets_created: status.created,
            tickets_updated: status.updated,
            errors: status.errors,
            message: `${status.created} billet(s) créé(s), ${status.updated} mis à jour`
          };
        } else if (status.status === 'failed') {
          return {
            success: false,
            mode: 'async',
            task_id: taskId,
            errors: status.errors,
            message: status.errors[0] || 'Import échoué'
          };
        }
        
        // Wait 1 second before next poll
        await new Promise(resolve => setTimeout(resolve, 1000));
        pollCount++;
      } catch (error) {
        console.error('Poll error:', error);
        pollCount++;
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }
    
    // Timeout
    return {
      success: false,
      mode: 'async',
      task_id: taskId,
      message: 'Import timeout - vérifiez le statut manuellement'
    };
  },

  // Get import task status
  getImportStatus: async (taskId) => {
    const response = await api.get(`/email/import-status/${taskId}`);
    return response.data;
  },

  // List all import tasks
  getImportTasks: async () => {
    const response = await api.get('/email/import-tasks');
    return response.data;
  },

  // Fetch from all accounts - LONG OPERATION
  fetchAllAccounts: async (project = 'WORLD CUP 2026') => {
    const response = await apiLong.post('/email/fetch-all', null, { params: { project } });
    return response.data;
  },

  // Auto-sync emails (background polling) - lighter than fetch-all
  autoSyncEmails: async (project = 'WORLD CUP 2026', forceRescan = false) => {
    const response = await api.post('/email/auto-sync', null, { params: { project, force_rescan: forceRescan } });
    return response.data;
  },

  // Force re-scan all emails (vérifie même les emails déjà traités)
  forceRescanEmails: async (project = 'WORLD CUP 2026') => {
    const response = await api.post('/email/auto-sync', null, { params: { project, force_rescan: true } });
    return response.data;
  },

  // ==================== CUSTOM PLATFORMS ====================
  
  // Get custom platforms
  getCustomPlatforms: async () => {
    const response = await api.get('/platforms/custom');
    return response.data;
  },

  // Add custom platform
  addCustomPlatform: async (platform) => {
    const response = await api.post('/platforms/custom', platform);
    return response.data;
  },

  // Delete custom platform
  deleteCustomPlatform: async (platformId) => {
    const response = await api.delete(`/platforms/custom/${platformId}`);
    return response.data;
  },

  // Get built-in platforms
  getBuiltinPlatforms: async () => {
    const response = await api.get('/platforms/builtin');
    return response.data;
  },

  // ==================== SNAPSHOTS / HISTORIQUE ====================
  
  // Get snapshots list
  getSnapshots: async (project = 'WORLD CUP 2026') => {
    const response = await api.get('/snapshots', { params: { project } });
    return response.data;
  },

  // Create a snapshot
  createSnapshot: async (project = 'WORLD CUP 2026', reason = 'Sauvegarde manuelle') => {
    const response = await api.post('/snapshots/create', null, { params: { project, reason } });
    return response.data;
  },

  // Restore a snapshot
  restoreSnapshot: async (snapshotId, project = 'WORLD CUP 2026') => {
    const response = await api.post(`/snapshots/restore/${snapshotId}`, null, { params: { project } });
    return response.data;
  },

  // Cleanup old logs
  cleanupLogs: async () => {
    const response = await api.delete('/logs/cleanup');
    return response.data;
  },

  // Desattribuer ALL tickets (with snapshot backup)
  desattribuerAllWithBackup: async (project = 'WORLD CUP 2026', userEmail = 'SYSTÈME') => {
    const response = await api.post('/tickets/desattribuer-all', null, { 
      params: { project, user_email: userEmail, skip_snapshot: true } 
    });
    return response.data;
  },

  // ==================== MANUAL ATTRIBUTION ====================
  
  // Get available ACHAT tickets for a VENTE (same category)
  getAvailableAchats: async (venteId, project = 'WORLD CUP 2026') => {
    const response = await api.get(`/tickets/available-achats/${venteId}`, { params: { project } });
    return response.data;
  },

  // Perform manual attribution (same category)
  manualAttribution: async (venteId, achatIds, validate = false, project = 'WORLD CUP 2026') => {
    const response = await api.post('/tickets/manual-attribution', {
      vente_id: venteId,
      achat_ids: achatIds,
      validate: validate
    }, { params: { project } });
    return response.data;
  },

  // ==================== SPECIAL ATTRIBUTION (DIFFERENT CATEGORY) ====================
  
  // Get ALL available ACHAT tickets for a VENTE's event (ANY category)
  getSpecialAttributionAchats: async (venteId, project = 'WORLD CUP 2026') => {
    const response = await api.get(`/tickets/special-attribution/${venteId}`, { params: { project } });
    return response.data;
  },

  // Perform special attribution (different category)
  specialAttribution: async (venteId, achatIds, validate = false, project = 'WORLD CUP 2026') => {
    const response = await api.post('/tickets/special-attribution', {
      vente_id: venteId,
      achat_ids: achatIds,
      validate: validate,
      force_different_category: true
    }, { params: { project } });
    return response.data;
  },

  // ==================== VÉRIFICATION DE RÉFÉRENCES ====================
  
  // Check a list of references
  checkReferences: async (references, project = 'WORLD CUP 2026') => {
    const response = await api.post('/tickets/check-refs', { references }, { params: { project } });
    return response.data;
  },

  // Fix mixed source attributions - OPTIMIZED
  fixMixedSources: async (project = 'WORLD CUP 2026') => {
    const response = await api.post('/tickets/fix-mixed-sources', null, { 
      params: { project, skip_snapshot: true },
      timeout: 120000
    });
    return response.data;
  },

  // Clean duplicate VENTE entries - OPTIMIZED
  cleanDuplicates: async (project = 'WORLD CUP 2026') => {
    const response = await api.post('/tickets/clean-duplicates', null, { 
      params: { project, skip_snapshot: true },
      timeout: 120000
    });
    return response.data;
  },

  // ==================== BACKGROUND SYNC (Server-side 24/7) ====================
  
  // Start background sync on server
  startBackgroundSync: async (intervalMinutes = 5, project = 'WORLD CUP 2026') => {
    const response = await api.post('/background-sync/start', null, { 
      params: { interval_minutes: intervalMinutes, project } 
    });
    return response.data;
  },

  // Stop background sync on server
  stopBackgroundSync: async () => {
    const response = await api.post('/background-sync/stop');
    return response.data;
  },

  // Get background sync status
  getBackgroundSyncStatus: async () => {
    const response = await api.get('/background-sync/status');
    return response.data;
  },

  // Update background sync interval
  updateBackgroundSyncInterval: async (intervalMinutes) => {
    const response = await api.put('/background-sync/interval', null, { 
      params: { interval_minutes: intervalMinutes } 
    });
    return response.data;
  },

  // ============ STADIUM MAPPING ============
  
  // Get stadium mappings
  getStadiumMappings: async () => {
    const response = await api.get('/stadium-mappings');
    return response.data;
  },

  // Save stadium mappings
  saveStadiumMappings: async (mappings) => {
    const response = await api.post('/stadium-mappings', mappings);
    return response.data;
  },

  // ============ GOOGLE SHEETS IMPORT ============
  
  // Test if a Google Sheet is accessible
  testGoogleSheetAccess: async (url) => {
    const response = await api.get('/google-sheets/test-access', { params: { url } });
    return response.data;
  },

  // Preview Google Sheet data
  previewGoogleSheet: async (url, ticketType, project = 'WORLD CUP 2026') => {
    const response = await api.post('/google-sheets/preview', {
      url,
      ticket_type: ticketType,
      project
    });
    return response.data;
  },

  // Import from Google Sheet
  importGoogleSheet: async (url, ticketType, columnMapping, project = 'WORLD CUP 2026') => {
    const response = await api.post('/google-sheets/import', {
      url,
      ticket_type: ticketType,
      column_mapping: columnMapping,
      project
    });
    return response.data;
  },
  normalizeRGMatches: async (project = 'ROLAND GARROS') => {
    const response = await api.post(`/normalize-rg-matches?project=${encodeURIComponent(project)}`);
    return response.data;
  },
};

export default api;
